// src/kit.js — entry point. Bundled by esbuild to dist/kit.js as a classic IIFE
// that exposes `window.AqViz`. No eval, no runtime module system.

import { h, render, options } from 'preact';
import htm from 'htm';
import { createDataHelper } from './data.js';
import { createListDataHelper } from './listData.js';
import { createPageDataHelper } from './pageData.js';
import { createComponents } from './components.js';
import { createAutoView } from './autoView.js';
import { createAutoCardView } from './autoCardView.js';
import { createAutoListView } from './autoListView.js';
import { createAutoPageView } from './autoPageView.js';

const html = htm.bind(h);

// Defensive degradation: if a view references a component that was never defined — a name mismatch, or a
// custom components/{key}.js that failed to load (e.g. a stale cached kit, or a runtime error in the file) —
// htm produces h(undefined, …). Preact would surface that vnode as the literal text "[object Object]",
// corrupting the page. Replace any undefined-type vnode with a no-op so ONE missing component degrades to
// nothing instead of breaking the whole view.
const MissingComponent = () => null;
const _prevVnodeHook = options.vnode;
options.vnode = (vnode) => {
  if (vnode && vnode.type === undefined) {
    if (typeof console !== 'undefined' && console.warn) console.warn('[AqViz] a referenced component is undefined — rendering nothing in its place.');
    vnode.type = MissingComponent;
  }
  if (_prevVnodeHook) _prevVnodeHook(vnode);
};
const components = createComponents(html);
const autoView = createAutoView(html, components);
const autoCardView = createAutoCardView(html, components);
const autoListView = createAutoListView(html, components);
const autoPageView = createAutoPageView(html, components);

// One small base stylesheet injected once. Inline `.aq-*` classes only — no CDN.
// A :root design-token layer drives every rule; each var() carries an inline #fallback so the original
// look survives even if :root is ever stripped (shadow DOM, sanitizers). Class NAMES and structure are
// the stable contract authored views depend on — only values were tokenized + a few additive modifier
// classes appended, so existing per-form views re-skin without breaking.
const BASE_CSS = `
:root{
--aq-primary:#2563eb;--aq-primary-dk:#1e3a8a;
--aq-ink:#0f172a;--aq-ink-2:#475569;--aq-muted:#64748b;--aq-faint:#94a3b8;
--aq-surface:#fff;--aq-bg:#f8fafc;--aq-bg-2:#f1f5f9;--aq-zebra:#fafbfc;--aq-border:#e2e8f0;--aq-track:#eef2f7;
--aq-radius:14px;--aq-radius-card:12px;--aq-radius-sm:10px;--aq-radius-pill:999px;
--aq-shadow-sm:0 1px 2px rgba(15,23,42,.04),0 1px 3px rgba(15,23,42,.05);
--aq-shadow-md:0 4px 12px -2px rgba(15,23,42,.10),0 2px 6px -2px rgba(15,23,42,.06);
--aq-shadow-hero:0 10px 30px -8px rgba(37,99,235,.35);
--aq-ok-bg:#f0fdf4;--aq-ok-fg:#166534;--aq-ok-bd:#bbf7d0;--aq-ok-solid:#16a34a;
--aq-warn-bg:#fffbeb;--aq-warn-fg:#92400e;--aq-warn-bd:#fde68a;--aq-warn-solid:#d97706;
--aq-err-bg:#fef2f2;--aq-err-fg:#991b1b;--aq-err-bd:#fecaca;--aq-err-solid:#dc2626;
--aq-grad-hero:linear-gradient(135deg,#1e3a8a,#2563eb);--aq-grad-list:linear-gradient(135deg,#0f766e,#0891b2);
}
.aq-doc{max-width:960px;margin:0 auto;padding:16px;color:var(--aq-ink,#0f172a);font:15px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased}
.aq-hero{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;padding:20px 22px;margin-bottom:18px;border-radius:var(--aq-radius,14px);background:var(--aq-grad-hero,linear-gradient(135deg,#1e3a8a,#2563eb));color:#fff;box-shadow:var(--aq-shadow-hero,0 6px 24px rgba(37,99,235,.18))}
.aq-hero-t{margin:0;font-size:24px;font-weight:700;line-height:1.2;letter-spacing:-.01em}
.aq-hero-sub{margin:4px 0 0;opacity:.92;font-size:14px}
.aq-hero-meta{display:flex;flex-wrap:wrap;gap:14px;margin-top:12px;font-size:13px;opacity:.95}
.aq-hero-meta span{white-space:nowrap}
.aq-badge{display:inline-block;padding:4px 12px;border-radius:var(--aq-radius-pill,999px);font-size:12px;font-weight:600;background:rgba(255,255,255,.22);color:#fff;text-transform:capitalize}
.aq-badge-ok{background:#dcfce7;color:#166534}
.aq-badge-warn{background:#fef3c7;color:#92400e}
.aq-badge-err{background:#fee2e2;color:#991b1b}
.aq-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:18px}
.aq-kpi{padding:16px;border-radius:var(--aq-radius-card,12px);background:var(--aq-surface,#fff);border:1px solid var(--aq-border,#e2e8f0);box-shadow:var(--aq-shadow-sm,0 1px 2px rgba(15,23,42,.04));transition:box-shadow .15s ease,transform .15s ease}
.aq-kpi:hover{box-shadow:var(--aq-shadow-md,0 4px 12px -2px rgba(15,23,42,.10));transform:translateY(-1px)}
.aq-kpi-v{font-size:28px;font-weight:700;color:var(--aq-primary-dk,#1e3a8a);line-height:1.1;font-variant-numeric:tabular-nums}
.aq-kpi-u{font-size:15px;font-weight:600;color:var(--aq-muted,#64748b)}
.aq-kpi-l{margin-top:6px;font-size:12px;color:var(--aq-muted,#64748b);text-transform:uppercase;letter-spacing:.04em}
.aq-kpi--ok{border-color:var(--aq-ok-bd,#bbf7d0);background:var(--aq-ok-bg,#f0fdf4);box-shadow:inset 3px 0 0 var(--aq-ok-solid,#16a34a),var(--aq-shadow-sm,0 1px 2px rgba(15,23,42,.04))}
.aq-kpi--ok .aq-kpi-v{color:var(--aq-ok-fg,#166534)}
.aq-kpi--warn{border-color:var(--aq-warn-bd,#fde68a);background:var(--aq-warn-bg,#fffbeb);box-shadow:inset 3px 0 0 var(--aq-warn-solid,#d97706),var(--aq-shadow-sm,0 1px 2px rgba(15,23,42,.04))}
.aq-kpi--warn .aq-kpi-v{color:var(--aq-warn-fg,#92400e)}
.aq-kpi--err{border-color:var(--aq-err-bd,#fecaca);background:var(--aq-err-bg,#fef2f2);box-shadow:inset 3px 0 0 var(--aq-err-solid,#dc2626),var(--aq-shadow-sm,0 1px 2px rgba(15,23,42,.04))}
.aq-kpi--err .aq-kpi-v{color:var(--aq-err-fg,#991b1b)}
.aq-stat{padding:10px 14px;border-radius:var(--aq-radius-sm,10px);background:var(--aq-bg,#f8fafc);border:1px solid var(--aq-track,#eef2f7)}
.aq-stat-v{font-size:20px;font-weight:700;color:var(--aq-ink,#0f172a);font-variant-numeric:tabular-nums}
.aq-stat-l{font-size:12px;color:var(--aq-muted,#64748b)}
.aq-stat--ok{background:var(--aq-ok-bg,#f0fdf4);border-color:var(--aq-ok-bd,#bbf7d0);box-shadow:inset 3px 0 0 var(--aq-ok-solid,#16a34a)}
.aq-stat--ok .aq-stat-v{color:var(--aq-ok-fg,#166534)}
.aq-stat--warn{background:var(--aq-warn-bg,#fffbeb);border-color:var(--aq-warn-bd,#fde68a);box-shadow:inset 3px 0 0 var(--aq-warn-solid,#d97706)}
.aq-stat--warn .aq-stat-v{color:var(--aq-warn-fg,#92400e)}
.aq-stat--err{background:var(--aq-err-bg,#fef2f2);border-color:var(--aq-err-bd,#fecaca);box-shadow:inset 3px 0 0 var(--aq-err-solid,#dc2626)}
.aq-stat--err .aq-stat-v{color:var(--aq-err-fg,#991b1b)}
.aq-section{margin:0 0 22px}
.aq-section-t{font-size:16px;font-weight:700;color:var(--aq-ink,#0f172a);margin:0 0 10px;padding-bottom:6px;border-bottom:2px solid var(--aq-border,#e2e8f0)}
.aq-card{margin:0 0 16px;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-card,12px);background:var(--aq-surface,#fff);overflow:hidden;box-shadow:var(--aq-shadow-sm,0 1px 2px rgba(15,23,42,.04))}
.aq-card-t{margin:0;padding:12px 16px;font-size:14px;font-weight:700;background:var(--aq-bg,#f8fafc);border-bottom:1px solid var(--aq-border,#e2e8f0)}
.aq-card-b{padding:14px 16px}
.aq-dl{display:grid;grid-template-columns:1fr;gap:0;margin:0;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-sm,10px);overflow:hidden}
.aq-dl-row{display:grid;grid-template-columns:minmax(140px,34%) 1fr;gap:12px;padding:10px 14px;border-bottom:1px solid var(--aq-bg-2,#f1f5f9)}
.aq-dl-row:last-child{border-bottom:0}
.aq-dl-row:nth-child(even){background:var(--aq-zebra,#fafbfc)}
.aq-dl-t{margin:0;font-weight:600;color:var(--aq-ink-2,#475569)}
.aq-dl-d{margin:0;color:var(--aq-ink,#0f172a);word-break:break-word}
.aq-gallery{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:12px}
.aq-ga-item{margin:0;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-sm,10px);overflow:hidden;background:var(--aq-surface,#fff)}
.aq-ga-img{display:block;width:100%;height:150px;object-fit:cover;background:var(--aq-bg-2,#f1f5f9)}
.aq-ga-audio,.aq-ga-video{display:block;width:100%}
.aq-ga-video{height:150px;object-fit:cover;background:#000}
.aq-ga-file{display:flex;align-items:center;justify-content:center;height:80px;padding:8px;color:var(--aq-primary,#2563eb);text-decoration:none;font-size:13px;text-align:center}
.aq-ga-cap{padding:6px 8px;font-size:11px;color:var(--aq-muted,#64748b);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.aq-ga-tr{padding:0 8px 8px;font-size:11px;color:var(--aq-ink-2,#475569);font-style:italic}
.aq-tablewrap{overflow-x:auto;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-sm,10px)}
.aq-table{width:100%;border-collapse:collapse;font-size:13px}
.aq-table th{text-align:left;padding:9px 12px;background:var(--aq-bg,#f8fafc);font-weight:700;color:var(--aq-ink-2,#475569);border-bottom:2px solid var(--aq-border,#e2e8f0);white-space:nowrap}
.aq-table td{padding:9px 12px;border-bottom:1px solid var(--aq-bg-2,#f1f5f9);color:var(--aq-ink,#0f172a)}
.aq-table tr:last-child td{border-bottom:0}
.aq-table tr:nth-child(even) td{background:var(--aq-zebra,#fafbfc)}
.aq-chart{margin:12px 0;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-sm,10px);padding:8px 4px;background:var(--aq-surface,#fff)}
.aq-chart-legend{display:flex;flex-wrap:wrap;gap:12px;justify-content:center;padding:2px 8px 4px;font-size:12px;color:var(--aq-ink-2,#475569)}
.aq-chart-legend span{display:inline-flex;align-items:center;gap:6px;white-space:nowrap}
.aq-chart-legend i{width:10px;height:10px;border-radius:2px;display:inline-block;flex:none}
.aq-state{max-width:520px;margin:48px auto;padding:28px;text-align:center;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius,14px);background:var(--aq-surface,#fff);font:15px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:var(--aq-ink,#0f172a);box-shadow:var(--aq-shadow-sm,0 1px 2px rgba(15,23,42,.04))}
.aq-state-t{margin:0 0 6px;font-size:18px;font-weight:700}
.aq-state-p{margin:0;color:var(--aq-muted,#64748b);font-size:14px}
.aq-spinner{width:34px;height:34px;margin:0 auto 14px;border:3px solid var(--aq-border,#e2e8f0);border-top-color:var(--aq-primary,#2563eb);border-radius:50%;animation:aq-spin .8s linear infinite}
@keyframes aq-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.aq-spinner{animation:none}.aq-kpi{transition:none}}
.aq-statgrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:8px;margin-bottom:14px}
.aq-sg-cell{padding:10px 12px;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-sm,10px);background:var(--aq-surface,#fff)}
.aq-sg-v{font-size:18px;font-weight:700;color:var(--aq-primary-dk,#1e3a8a);line-height:1.1;font-variant-numeric:tabular-nums}
.aq-sg-u{font-size:12px;font-weight:600;color:var(--aq-muted,#64748b)}
.aq-sg-l{margin-top:4px;font-size:11px;color:var(--aq-muted,#64748b);text-transform:uppercase;letter-spacing:.03em}
.aq-sg-cell--ok{background:var(--aq-ok-bg,#f0fdf4);border-color:var(--aq-ok-bd,#bbf7d0);box-shadow:inset 3px 0 0 var(--aq-ok-solid,#16a34a)}
.aq-sg-cell--ok .aq-sg-v{color:var(--aq-ok-fg,#166534)}
.aq-sg-cell--warn{background:var(--aq-warn-bg,#fffbeb);border-color:var(--aq-warn-bd,#fde68a);box-shadow:inset 3px 0 0 var(--aq-warn-solid,#d97706)}
.aq-sg-cell--warn .aq-sg-v{color:var(--aq-warn-fg,#92400e)}
.aq-sg-cell--err{background:var(--aq-err-bg,#fef2f2);border-color:var(--aq-err-bd,#fecaca);box-shadow:inset 3px 0 0 var(--aq-err-solid,#dc2626)}
.aq-sg-cell--err .aq-sg-v{color:var(--aq-err-fg,#991b1b)}
.aq-donut{display:flex;flex-direction:column;align-items:center;gap:6px;margin:8px 0}
.aq-donut-l{font-size:12px;color:var(--aq-muted,#64748b);text-align:center}
.aq-cmp{display:flex;flex-direction:column;gap:8px;margin-bottom:14px}
.aq-cmp-row{display:grid;grid-template-columns:minmax(80px,30%) 1fr auto;gap:10px;align-items:center}
.aq-cmp-l{font-size:13px;color:var(--aq-ink-2,#475569)}
.aq-cmp-track{height:10px;border-radius:var(--aq-radius-pill,999px);background:var(--aq-track,#eef2f7);overflow:hidden}
.aq-cmp-fill{height:100%;border-radius:var(--aq-radius-pill,999px);background:var(--aq-primary,#2563eb)}
.aq-cmp-fill--ok{background:var(--aq-ok-solid,#16a34a)}
.aq-cmp-fill--warn{background:var(--aq-warn-solid,#d97706)}
.aq-cmp-fill--err{background:var(--aq-err-solid,#dc2626)}
.aq-cmp-v{font-size:13px;font-weight:600;color:var(--aq-ink,#0f172a);font-variant-numeric:tabular-nums}
.aq-callout{margin:0 0 14px;padding:12px 14px;border-radius:var(--aq-radius-sm,10px);border:1px solid var(--aq-border,#e2e8f0);border-left:4px solid var(--aq-faint,#94a3b8);background:var(--aq-bg,#f8fafc);color:var(--aq-ink,#0f172a)}
.aq-callout-ok{border-color:var(--aq-ok-bd,#bbf7d0);border-left-color:var(--aq-ok-solid,#16a34a);background:var(--aq-ok-bg,#f0fdf4)}
.aq-callout-warn{border-color:var(--aq-warn-bd,#fde68a);border-left-color:var(--aq-warn-solid,#d97706);background:var(--aq-warn-bg,#fffbeb)}
.aq-callout-err{border-color:var(--aq-err-bd,#fecaca);border-left-color:var(--aq-err-solid,#dc2626);background:var(--aq-err-bg,#fef2f2)}
.aq-callout-t{font-weight:700;margin-bottom:4px}
.aq-callout-b{font-size:13px;color:var(--aq-ink-2,#475569)}
.aq-loc{padding:12px 14px;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-sm,10px);background:var(--aq-bg,#f8fafc);margin:0 0 12px}
.aq-loc-l{font-size:11px;color:var(--aq-muted,#64748b);text-transform:uppercase;letter-spacing:.03em;margin-bottom:4px}
.aq-loc-name{font-weight:600;color:var(--aq-ink,#0f172a);margin-bottom:2px}
.aq-loc-co{font-size:13px;color:var(--aq-ink-2,#475569);font-variant-numeric:tabular-nums}
.aq-loc-co a{color:var(--aq-primary,#2563eb);text-decoration:none;white-space:nowrap}
.aq-loc-empty{color:var(--aq-faint,#94a3b8)}
.aq-map{margin:0 0 14px;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-card,12px);overflow:hidden;background:var(--aq-surface,#fff)}
.aq-map-view{position:relative;overflow:hidden;background:#e8eef3}
.aq-map-grid{position:absolute;width:0;height:0}
.aq-map-tile{position:absolute;width:256px;height:256px;max-width:none;border:0}
.aq-map-pin{position:absolute;left:50%;top:50%;transform:translate(-50%,-100%);font-size:24px;line-height:1;pointer-events:none;filter:drop-shadow(0 1px 1px rgba(0,0,0,.45))}
.aq-map-cap{display:flex;justify-content:space-between;align-items:center;gap:10px;padding:8px 12px;font-size:12px;color:var(--aq-muted,#64748b);border-top:1px solid var(--aq-bg-2,#f1f5f9);font-variant-numeric:tabular-nums}
.aq-map-cap a{color:var(--aq-primary,#2563eb);text-decoration:none;white-space:nowrap}
.aq-cardhead{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px}
.aq-cardhead-t{font-weight:700;font-size:15px;color:var(--aq-ink,#0f172a);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.aq-card-mode .aq-doc,.aq-card-mode.aq-doc{max-width:none;padding:10px}
.aq-card-mode .aq-hero{flex-direction:column;gap:6px;padding:10px 12px;margin-bottom:10px;border-radius:var(--aq-radius-sm,10px)}
.aq-card-mode .aq-hero-t{font-size:16px}
.aq-card-mode .aq-hero-sub{font-size:12px}
.aq-card-mode .aq-hero-meta{gap:8px;margin-top:6px;font-size:11px}
.aq-card-mode .aq-row{grid-template-columns:repeat(auto-fit,minmax(96px,1fr));gap:8px;margin-bottom:10px}
.aq-card-mode .aq-kpi{padding:10px}
.aq-card-mode .aq-kpi:hover{transform:none}
.aq-card-mode .aq-kpi-v{font-size:20px}
.aq-card-mode .aq-section{margin-bottom:12px}
.aq-card-mode .aq-chart{margin:6px 0}
.aq-card-mode .aq-statgrid{grid-template-columns:repeat(auto-fit,minmax(88px,1fr));gap:6px;margin-bottom:10px}
.aq-list .aq-hero,.aq-list-mode .aq-hero{background:var(--aq-grad-list,linear-gradient(135deg,#0f766e,#0891b2));box-shadow:0 10px 30px -8px rgba(8,145,178,.35)}
.aq-page,.aq-page-mode .aq-doc,.aq-page-mode.aq-doc{max-width:none;padding:0}
.aq-page-in{max-width:1120px;margin:0 auto;padding:0 16px}
.aq-login{max-width:420px;padding:16px;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-card,12px);background:var(--aq-surface,#fff);box-shadow:var(--aq-shadow-sm,0 1px 2px rgba(15,23,42,.04))}
.aq-login-t{font-weight:700;font-size:14px;color:var(--aq-ink,#0f172a);margin-bottom:10px}
.aq-login-row{display:flex;align-items:center;gap:10px;margin-bottom:8px}
.aq-login-k{width:72px;font-size:12px;color:var(--aq-muted,#64748b);text-transform:uppercase;letter-spacing:.03em}
.aq-login-v{flex:1;min-width:0;padding:6px 10px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:13px;color:var(--aq-ink,#0f172a);background:var(--aq-bg,#f8fafc);border:1px solid var(--aq-track,#eef2f7);border-radius:var(--aq-radius-sm,10px);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.aq-copy{flex:none;cursor:pointer;padding:6px 12px;font-size:12px;font-weight:600;color:var(--aq-primary,#2563eb);background:var(--aq-bg,#f8fafc);border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-sm,10px)}
.aq-copy:hover{background:var(--aq-bg-2,#f1f5f9)}
.aq-login-note{margin-top:6px;font-size:12px;color:var(--aq-muted,#64748b)}
.aq-embed{margin:0 0 16px}
.aq-embed-t{font-weight:600;font-size:13px;color:var(--aq-ink-2,#475569);margin-bottom:6px}
.aq-embed-frame{display:block;width:100%;min-height:200px;border:1px solid var(--aq-border,#e2e8f0);border-radius:var(--aq-radius-card,12px);background:var(--aq-surface,#fff)}
.aq-phone{display:inline-block;max-width:280px;padding:10px;background:var(--aq-ink,#0f172a);border-radius:32px;box-shadow:var(--aq-shadow-md,0 4px 12px -2px rgba(15,23,42,.10))}
.aq-phone-screen{border-radius:22px;overflow:hidden;background:#000}
.aq-phone-screen img{display:block;width:100%;height:auto}
.aq-cta{display:inline-block;margin:0 0 18px;padding:12px 22px;border-radius:var(--aq-radius-pill,999px);background:var(--aq-primary,#2563eb);color:#fff;font-weight:700;font-size:15px;text-decoration:none;box-shadow:var(--aq-shadow-hero,0 6px 24px rgba(37,99,235,.18))}
.aq-cta:hover{background:var(--aq-primary-dk,#1e3a8a)}
@media (max-width:380px){.aq-row{grid-template-columns:1fr}.aq-hero{flex-direction:column;gap:8px;padding:14px}.aq-hero-t{font-size:20px}}
`;

let cssInjected = false;
function injectCss() {
  if (cssInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.id = 'aq-viz-base';
  style.textContent = BASE_CSS;
  (document.head || document.documentElement).appendChild(style);
  cssInjected = true;
}

function resolveMount(mount) {
  if (typeof document === 'undefined') return null;
  if (mount && typeof mount === 'object' && mount.nodeType === 1) return mount;
  const sel = typeof mount === 'string' && mount ? mount : '#root';
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement('div');
    el.id = sel.replace(/^#/, '') || 'root';
    document.body.appendChild(el);
  }
  return el;
}

function LoadingState() {
  return html`<div class="aq-state"><div class="aq-spinner" aria-hidden="true"></div>
    <p class="aq-state-t">Loading submission…</p>
    <p class="aq-state-p">Fetching visualization data.</p></div>`;
}

function ErrorState({ message }) {
  return html`<div class="aq-state" role="alert">
    <p class="aq-state-t">Unable to display this submission</p>
    <p class="aq-state-p">${message || 'The data could not be loaded. Please try again later.'}</p></div>`;
}

// Auto-height: when embedded in an iframe, post the document height to the parent so the host page can
// size the iframe to its content (no scrollbars, no clipping). A top-level serve (/v/{token}) has
// `parent === window`, so this is a no-op there. Backward compatible / purely additive.
function postHeight() {
  if (typeof window === 'undefined' || window.parent === window) return;
  try {
    var de = document.documentElement, b = document.body;
    var h = Math.ceil(Math.max(de ? de.scrollHeight : 0, b ? b.scrollHeight : 0));
    if (h > 0) window.parent.postMessage({ type: 'aqrate.viz.resize', height: h }, '*');
  } catch (_e) {}
}

// When the authored view throws while rendering, surface the real error to the embedding host (the editor) so it
// can show details and offer a "Fix with AI" action. No-op on a top-level serve (parent === window). The host
// validates the message by event.source (this iframe), mirroring the resize channel.
function postRenderError(err) {
  if (typeof window === 'undefined' || window.parent === window) return;
  try {
    window.parent.postMessage({
      type: 'aqrate.viz.error',
      message: (err && err.message) ? String(err.message) : 'The visualization failed to render.',
      stack: (err && err.stack) ? String(err.stack) : ''
    }, '*');
  } catch (_e) {}
}

let heightObserver = null;
let loadListenerAdded = false;
// After a render, emit height immediately, then again after a frame and on delayed fallbacks (so images /
// async layout settle), observe the mount for later reflows, and catch the window 'load' once.
function wireAutoHeight(mountEl) {
  postHeight();
  if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(postHeight);
  setTimeout(postHeight, 200);
  setTimeout(postHeight, 700);
  setTimeout(postHeight, 1500); // catch late font / image reflow

  if (typeof ResizeObserver !== 'undefined' && !heightObserver) {
    try {
      heightObserver = new ResizeObserver(function () { postHeight(); });
      if (mountEl) heightObserver.observe(mountEl);
      if (typeof document !== 'undefined' && document.body) heightObserver.observe(document.body);
    } catch (_e) { heightObserver = null; }
  }

  if (typeof window !== 'undefined' && !loadListenerAdded) {
    try {
      window.addEventListener('load', postHeight);
      window.addEventListener('resize', postHeight);
      loadListenerAdded = true;
    } catch (_e) {}
  }
}

// Single registered per-form view function: (d) => vnode.
let registeredView = null;
function register(viewFn) {
  if (typeof viewFn === 'function') registeredView = viewFn;
  return AqViz;
}

// Custom per-visualization components attach themselves to the AqViz namespace via define(), so a view
// authored for THIS form can destructure them exactly like the built-ins. Custom component files are inlined
// BEFORE the view script (see the assembler), so the name exists by the time the view destructures it:
//   // components/roomScorecard.js
//   const { html, define } = window.AqViz;
//   define('RoomScorecard', (props) => html`<div class="aq-card">…</div>`);
//   // view.js
//   const { html, register, RoomScorecard, KPI } = window.AqViz;
//   register((d) => html`<${RoomScorecard} rows=${d.repeated('checks')} />`);
function define(name, fn) {
  if (name && typeof name === 'string' && typeof fn === 'function') AqViz[name] = fn;
  return AqViz;
}

async function boot(opts) {
  const options = opts || {};
  injectCss();
  const mountEl = resolveMount(options.mount);
  if (!mountEl) return;

  // Compact card variant: the server passes variant:'card' when the resolved viz is a Card. It tightens an
  // authored full view via CSS and selects a compact fallback when no view is registered. Default (undefined)
  // is byte-for-byte the original behavior.
  const isCard = options.variant === 'card';
  if (isCard && mountEl.classList) mountEl.classList.add('aq-card-mode');

  render(html`<${LoadingState} />`, mountEl);

  let payload = null;
  try {
    if (options.payload && typeof options.payload === 'object') {
      // Local / inlined shortcut — render with zero network.
      payload = options.payload;
    } else if (options.dataUrl) {
      const res = await fetch(options.dataUrl, { headers: { accept: 'application/json' } });
      if (!res.ok) {
        render(html`<${ErrorState} message=${'The submission data is currently unavailable.'} />`, mountEl);
        return;
      }
      payload = await res.json();
    } else {
      render(html`<${ErrorState} message=${'No data source was provided.'} />`, mountEl);
      return;
    }
  } catch (_err) {
    render(html`<${ErrorState} message=${'We could not reach the data service.'} />`, mountEl);
    return;
  }

  try {
    // Dispatch on payload shape (sv10): a PAGE payload (kind/variant:"page") gets the page data-helper +
    // auto-page view and a widened, full-bleed-capable shell; a plural LIST payload (kind:"list", or a
    // submissions[] array) gets the list helper + auto-list view; a single submission keeps the exact
    // original path. Uploaded images/screenshots arrive as a name → data-URI map (server-inlined; never a
    // public URL), exposed via d.asset().
    const isPage = !!(payload && (payload.kind === 'page' || payload.variant === 'page')) || options.variant === 'page';
    const isList = !isPage && !!(payload && (payload.kind === 'list' || Array.isArray(payload.submissions)));
    let d;
    let view;
    if (isPage) {
      if (mountEl.classList) mountEl.classList.add('aq-page-mode');
      d = createPageDataHelper(payload, options.assets);
      view = registeredView || autoPageView;
    } else if (isList) {
      if (mountEl.classList) mountEl.classList.add('aq-list-mode');
      d = createListDataHelper(payload, options.assets);
      view = registeredView || autoListView;
    } else {
      d = createDataHelper(payload, options.assets);
      view = registeredView || (isCard ? autoCardView : autoView);
    }
    const vnode = view(d);
    render(vnode, mountEl);
    wireAutoHeight(mountEl);
  } catch (_err) {
    postRenderError(_err);
    render(html`<${ErrorState} message=${'Something went wrong while rendering this submission.'} />`, mountEl);
  }
}

const AqViz = {
  version: 'v1',
  // Kit assembly marker — bumped alongside the API's StandaloneVizAssembler. sv10 adds the page variant
  // (createPageDataHelper + autoPageView + LoginCard/DashboardEmbed/PhoneFrame/CTA gadgets); sv11 exposes
  // AqViz.copy(text) so an AI-authored page can build its own credentials card instead of the fixed LoginCard.
  assembly: 'sv11',
  html,
  h,
  render,
  register,
  define,
  boot,
  createDataHelper,
  createListDataHelper,
  createPageDataHelper,
  autoView,
  autoCardView,
  autoListView,
  autoPageView,
  ...components
};

if (typeof window !== 'undefined') window.AqViz = AqViz;
if (typeof globalThis !== 'undefined') globalThis.AqViz = AqViz;

export default AqViz;
